import { Fragment } from 'react'
import { isGroup } from '../filterSpec'
import { n, pct } from '../format'
import { dupMode } from './DuplicatePanel'
import { compareMode } from '../compareSpec'

/**
 * What you are looking at, spelled out left to right in the order it actually
 * happens: the whole file, then each filter, then the duplicate pass, then the
 * count that survived. Every step carries its own ✕, so undoing any one of them
 * is a single click and the question "how did I get to this number?" is always
 * answered on screen.
 *
 * The order is not decoration — filters run first, the duplicate pass runs over
 * whatever they selected, and insights count what that leaves.
 */

/**
 * A comparison has three stages worth naming, since which one is running says
 * what it is waiting on: the view's own scan, then the uploaded file, then the
 * pairing of the two.
 */
const COMPARE_PHASE = {
  view: 'scanning the view',
  file: 'reading the upload',
  match: 'matching rows',
}

/** A rule in the same words the filter panel used to build it. */
function phrase(node, byId) {
  if (isGroup(node)) {
    const join = ` ${node.combinator.toLowerCase()} `
    return `(${node.rules.map((child) => phrase(child, byId)).join(join)})`
  }
  const op = byId.get(node.op)
  const label = op?.label ?? node.op
  const arity = op?.arity ?? 1
  if (arity === 0) return `${node.column} ${label}`
  if (arity === 2) return `${node.column} ${label} ${node.value || '?'} and ${node.value2 || '?'}`
  if (arity === 'list') {
    const values = node.values ?? []
    if (values.length === 0) return `${node.column} ${label} nothing`
    const head = values.slice(0, 2).join(', ')
    return values.length > 2
      ? `${node.column} ${label} ${head} +${values.length - 2} more`
      : `${node.column} ${label} ${head}`
  }
  return `${node.column} ${label} ${node.value === '' ? '(blank)' : node.value}`
}

export function ScopeTrail({
  dataset,
  operators,
  applied,
  onDropFilter,
  dupApplied,
  onDropDup,
  compareApplied,
  onDropCompare,
  insightApplied,
  onDropInsight,
  status,
  resolutionNote,
  viewNote,
  onDismissNote,
  maxRetained,
}) {
  const byId = new Map(operators.map((o) => [o.id, o]))
  const scanning = !status.done
  const mode = dupApplied ? dupMode(dupApplied.mode) : null

  const steps = [
    {
      key: 'base',
      kind: 'base',
      text: dataset.indexed && dataset.rows > 0 ? `all ${n(dataset.rows)} rows` : dataset.name,
      title: dataset.path,
    },
    ...(applied?.rules ?? []).map((node) => ({
      key: node.id,
      kind: 'filter',
      text: phrase(node, byId),
      onDrop: () => onDropFilter(node.id),
      dropTitle: 'Remove this filter',
    })),
  ]

  if (dupApplied) {
    steps.push({
      key: 'dup',
      kind: 'dup',
      text: `${mode.verb.toLowerCase()} ${dupApplied.columns.join(' + ')}`,
      title: resolutionNote ? `${mode.blurb} — ${resolutionNote}` : mode.blurb,
      onDrop: onDropDup,
      dropTitle: 'Stop grouping by this key and show every row',
    })
  }
  // The comparison comes after the duplicate pass, because what it compares is
  // whatever that pass left — the trail spells the order out because it is real.
  if (compareApplied) {
    const cmp = compareMode(compareApplied.mode)
    steps.push({
      key: 'compare',
      kind: 'compare',
      text: `${cmp.verb} ${compareApplied.fileName ?? status.fileName ?? 'the uploaded file'}`,
      title: status.summary ? `${cmp.blurb} — matched ${status.summary}` : cmp.blurb,
      onDrop: onDropCompare,
      dropTitle: 'Stop comparing and go back to the rows',
    })
  }
  if (insightApplied) {
    steps.push({
      key: 'insight',
      kind: 'insight',
      text: `counted by ${insightApplied.columns.join(', ')}`,
      onDrop: onDropInsight,
      dropTitle: 'Back to the rows',
    })
  }

  return (
    <div className="trail">
      <span className="trail-lead">Showing</span>

      {steps.map((step, i) => (
        <Fragment key={step.key}>
          {i > 0 && (
            <span className="trail-arrow" aria-hidden="true">
              →
            </span>
          )}
          <span className={`trail-step ${step.kind}`} title={step.title ?? step.text}>
            <span className="trail-text">{step.text}</span>
            {step.onDrop && (
              <button
                className="trail-drop"
                onClick={step.onDrop}
                title={step.dropTitle}
                aria-label={step.dropTitle}
              >
                ×
              </button>
            )}
          </span>
        </Fragment>
      ))}

      <div className="trail-tail">
        {scanning && (
          <div className="scan">
            <div className="bar">
              <div className="bar-fill" style={{ width: pct(status.progress) }} />
            </div>
            <span className="scan-label">
              {compareApplied
                ? `${COMPARE_PHASE[status.phase] ?? 'comparing'} · ${pct(status.progress)}`
                : dupApplied
                  ? `pass ${status.pass ?? 1} of 2 · ${pct(status.progress)}`
                  : status.filtered
                    ? `scanning ${pct(status.progress)}`
                    : `indexing ${pct(status.progress)}`}
            </span>
          </div>
        )}
      </div>

      {status.capped &&
        (compareApplied ? (
          // A filtered or duplicate export re-reads the file and writes every
          // match, so the cap is only about browsing. A comparison is different:
          // it could only pair the rows its scan retained, so the cap is in the
          // answer itself and must not be described as a display limit.
          <span
            className="warn"
            title={`The view stops at ${n(maxRetained)} rows (CSV_MAX_ROWS), so rows past that were never compared. Narrow the filters to bring it under the cap.`}
          >
            partial comparison — only the first {n(maxRetained)} rows of the view were checked
          </span>
        ) : (
          <span
            className="warn"
            title={`Browsing stops at ${n(maxRetained)} rows; an export still writes every one.`}
          >
            showing the first {n(status.available)} — export writes all of them
          </span>
        ))}
      {status.keysCapped && (
        <span
          className="warn"
          title="Too many distinct repeated values to track exactly. Narrow the filters, or pick a more specific key."
        >
          partial result — too many distinct values
        </span>
      )}
      {status.error && <span className="warn">{status.error}</span>}
      {viewNote && (
        <button className={`note ${viewNote.kind}`} onClick={onDismissNote} title="Dismiss">
          {viewNote.text} ×
        </button>
      )}
    </div>
  )
}
